import { Grid } from "@material-ui/core";
import FormGroupTile from "components/core/basic/FormGroupTile";
import AudienceMultiplierField from "../fields/AudienceMultiplierField";
import PlacementModifierWarning from "./PlacementModifierWarning";
import { ALGORITHM_OPTIONS, AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES } from "../constants";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../../../../styled-system/css";
import { Checkbox } from "../../../../fields/base/Checkbox";

const SponsoredProductCampaignModifiers = ({
  state,
  onChange,
  classes,
  errors,
  fieldsToValidate,
  review,
  theme,
  bidStrategyOptions,
}) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const disablePlacementModifiers =
    state.algorithm === ALGORITHM_OPTIONS.PLACEMENT_MAX.value ||
    ([ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(state.algorithm) &&
      state.algoManagedPlacementMultipliers);

  return (
    <Grid
      container
      item
      xs={12}
      style={{
        marginBottom: theme.spacing(0),
      }}
    >
      <Grid item xs={12}>
        <h3 className={classes.subtitle}>Amazon Options:</h3>
      </Grid>
      {[ALGORITHM_OPTIONS.GROWTH_MAX.value, ALGORITHM_OPTIONS.ROAS_BOOST.value].includes(state.algorithm) ?
        <>
          <Grid
            item
            xs={12}
            md={5}
            className={css({
              padding: "small",
            })}
          >
            <Checkbox
              label="Algorithmically Manage Placement Multipliers"
              value={state.algoManagedPlacementMultipliers}
              onChange={(v) => {
                onChange({
                  algoManagedPlacementMultipliers: v,
                });
              }}
            />
          </Grid>
          <Grid xs={12} md={7} className={css({ paddingTop: "medium" })}>
            <p className={`${classes.explanationText} ${review && classes.reviewExplanationText}`}>
              Select this option to enable Placement Boost and let Trellis automatically optimize your placement
              multipliers.
            </p>
          </Grid>
        </>
      : null}
      <Grid
        item
        container
        xs={12}
        md={5}
        className={classes.inputContainer}
        style={{
          paddingTop: theme.spacing(1),
        }}
      >
        <Grid item xs={12} lg={6} style={{ padding: "0 2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            Bid Strategy
          </label>
          <FormGroupTile
            type="select"
            stateName={"bidStrategy"}
            handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
            disabled={false}
            defaultValue={bidStrategyOptions.find((strategy) => strategy.value === state.bidStrategy)}
            options={bidStrategyOptions}
          />
        </Grid>
        <Grid item xs={12} lg={6} style={{ padding: "0 2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            Top of Search
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"topOfSearchBidMultiplier"}
            handleChildFormElement={(key, value) => onChange({ [key]: value })}
            defaultValue={state.topOfSearchBidMultiplier}
            disabled={disablePlacementModifiers}
          />
        </Grid>

        <Grid item xs={12} lg={6} style={{ padding: "2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            Product Pages
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"productPageMultiplier"}
            handleChildFormElement={(key, value) => {
              onChange({ [key]: value });
            }}
            defaultValue={state.productPageMultiplier}
            disabled={disablePlacementModifiers}
          />
        </Grid>
        <Grid item xs={12} lg={6} style={{ padding: "2rem" }}>
          <label
            style={{
              color: "#73706E",
              fontWeight: "600",
              paddingBottom: ".7rem",
            }}
          >
            Rest of the Search
          </label>
          <FormGroupTile
            type="formcontrol"
            formControlType={"number"}
            prefix={"%"}
            prefixSide={"right"}
            stateName={"restOfSearchMultiplier"}
            handleChildFormElement={(key, value) => {
              onChange({ [key]: value });
            }}
            defaultValue={state.restOfSearchMultiplier}
            disabled={disablePlacementModifiers}
          />
        </Grid>
        {AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace) ?
          <Grid item xs={12} lg={6} style={{ padding: "0 2rem" }}>
            <label
              style={{
                color: "#73706E",
                fontWeight: "600",
                paddingBottom: ".7rem",
              }}
            >
              Amazon Business (All Placements)
            </label>
            <FormGroupTile
              type="formcontrol"
              formControlType={"number"}
              prefix={"%"}
              prefixSide={"right"}
              stateName={"amazonBusinessMultiplier"}
              handleChildFormElement={(key, value) => {
                onChange({ [key]: value });
              }}
              defaultValue={state.amazonBusinessMultiplier}
            />
          </Grid>
        : null}
        <Grid item xs={12} style={{ padding: "0 2rem" }}>
          {errors.topOfSearchBidMultiplier && fieldsToValidate?.includes("topOfSearchBidMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.topOfSearchBidMultiplier}
            </small>
          : errors.productPageMultiplier && fieldsToValidate?.includes("productPageMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.productPageMultiplier}
            </small>
          : errors.amazonBusinessMultiplier && fieldsToValidate?.includes("amazonBusinessMultiplier") ?
            <small className="text-danger" style={{ paddingTop: "1rem" }}>
              {errors.amazonBusinessMultiplier}
            </small>
          : errors.restOfSearchMultiplier &&
            fieldsToValidate?.includes("restOfSearchMultiplier") && (
              <small className="text-danger" style={{ paddingTop: "1rem" }}>
                {errors.restOfSearchMultiplier}
              </small>
            )
          }
        </Grid>
      </Grid>
      <Grid xs={12} md={7}>
        <p
          className={`${classes.explanationText} ${review && classes.reviewExplanationText}`}
          style={{ paddingTop: "2rem" }}
        >
          Let Amazon automatically bid more when doing so will bump your product to a relevant product page's top spot.
        </p>
        <PlacementModifierWarning state={state} review={review} />
      </Grid>

      <Grid xs={12} md={5}>
        <AudienceMultiplierField
          classes={classes}
          state={state}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          isMediaPlanField={true}
        />
      </Grid>
      <Grid xs={12} md={7}>
        <p
          className={`${classes.explanationText} ${review && classes.reviewExplanationText}`}
          style={{ paddingTop: "2rem" }}
        >
          Apply a bid multiplier when a shopper matches a target audience.
        </p>
        <p
          className={`${classes.explanationText} ${review && classes.reviewExplanationText}`}
          style={{ paddingTop: "1rem" }}
        >
          Not seeing your AMC Audience? AMC Audiences can take up to 48 hours to be available in advertising.
        </p>
      </Grid>
    </Grid>
  );
};
export default SponsoredProductCampaignModifiers;
